import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Phone, Building, FileText, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';

const BusinessRegisterForm = () => {
  const navigate = useNavigate();
  const { registerBusiness } = useAuth();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    companyName: '',
    vatNumber: '',
    fiscalCode: '',
    pec: '',
    businessType: '',
    codiceUnivoco: '',
    address: '',
    zipCode: '',
    city: '',
    province: '',
    marketingConsent: false,
    termsAccepted: false,
    privacyAccepted: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Nome è richiesto';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Cognome è richiesto';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email è richiesta';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email non valida';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password è richiesta';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password deve essere di almeno 8 caratteri';
    } else if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password deve contenere almeno una lettera e un numero';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Le password non coincidono';
    }
    
    if (!formData.phone) {
      newErrors.phone = 'Numero di telefono è richiesto';
    } else {
      // Rimuovi spazi, trattini e parentesi
      const cleanPhone = formData.phone.replace(/[\s\-()]/g, '');
      
      // Accetta formati: +393451773300, 00393451773300, 393451773300, 3451773300
      const phoneRegex = /^(\+39|0039|39)?[3]\d{8,9}$/;
      
      if (!phoneRegex.test(cleanPhone)) {
        newErrors.phone = 'Numero di telefono non valido (es: +39 345 1773300)';
      }
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Ragione sociale è richiesta';
    }

    if (!formData.vatNumber.trim()) {
      newErrors.vatNumber = 'Partita IVA è richiesta';
    } else if (!/^\d{11}$/.test(formData.vatNumber.replace(/\s/g, ''))) {
      newErrors.vatNumber = 'Partita IVA non valida (inserisci 11 cifre)';
    }

    if (!formData.businessType) {
      newErrors.businessType = 'Tipo di attività è richiesto';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Indirizzo è richiesto';
    }
    
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'CAP è richiesto';
    } else if (!/^\d{5}$/.test(formData.zipCode)) {
      newErrors.zipCode = 'CAP deve essere di 5 cifre';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'Città è richiesta';
    }
    
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'Devi accettare i termini e condizioni';
    }
    
    if (!formData.privacyAccepted) {
      newErrors.privacyAccepted = 'Devi accettare la privacy policy';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const registrationData = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        businessType: formData.businessType,
        // Business info should be flat, not nested
        companyName: formData.companyName,
        vatNumber: formData.vatNumber.replace(/\s/g, ''),
        fiscalCode: formData.fiscalCode,
        pec: formData.pec,
        codiceUnivoco: formData.codiceUnivoco,
        address: formData.address,
        zipCode: formData.zipCode,
        city: formData.city,
        marketingConsent: formData.marketingConsent,
        termsAccepted: formData.termsAccepted,
        privacyAccepted: formData.privacyAccepted
      };

      const result = await registerBusiness(registrationData);
      
      if (result.success) {
        toast({
          title: "Registrazione completata!",
          description: "Il tuo account aziendale è stato creato con successo. Verifica la tua email per attivare l'account.",
          variant: "success"
        });
        
        navigate('/dashboard');
      } else {
        toast({
          title: "Errore nella registrazione",
          description: result.message || "Si è verificato un errore. Riprova.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: "Errore nella registrazione",
        description: "Si è verificato un errore imprevisto. Riprova.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full mx-auto space-y-8">
        <div>
          <div className="flex justify-center">
            <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Registrazione Azienda
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Crea il tuo account business per IlTuoAmaro.it
          </p>
          <div className="mt-4 flex justify-center space-x-4">
            <Link
              to="/register/private"
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Account Privato
            </Link>
            <span className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg">
              Account Azienda
            </span>
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Personal Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  Nome *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                      errors.firstName ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                    placeholder="Nome"
                  />
                  <User className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                </div>
                {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Cognome *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                      errors.lastName ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                    placeholder="Cognome"
                  />
                  <User className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                </div>
                {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email aziendale *
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                  placeholder="email@azienda.it"
                />
                <Mail className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Telefono *
              </label>
              <div className="mt-1 relative">
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                  placeholder="+39 123 456 7890"
                />
                <Phone className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              </div>
              {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
            </div>

            {/* Business Info */}
            <div className="border-t pt-4 mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informazioni Azienda</h3>
              
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                  Ragione Sociale *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                      errors.companyName ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                    placeholder="Nome Azienda SRL"
                  />
                  <Building className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                </div>
                {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>}
              </div>

              <div className="mt-4">
                <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-700">
                  Partita IVA *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="vatNumber"
                    name="vatNumber"
                    type="text"
                    value={formData.vatNumber}
                    onChange={handleInputChange}
                    className={`appearance-none relative block w-full px-3 py-2 pl-10 border ${
                      errors.vatNumber ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                    placeholder="IT12345678901"
                  />
                  <FileText className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                </div>
                {errors.vatNumber && <p className="mt-1 text-sm text-red-600">{errors.vatNumber}</p>}
              </div>

              <div className="mt-4">
                <label htmlFor="businessType" className="block text-sm font-medium text-gray-700">
                  Tipo di Attività *
                </label>
                <select
                  id="businessType"
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleInputChange}
                  className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                    errors.businessType ? 'border-red-500' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                >
                  <option value="">Seleziona tipo di attività</option>
                  <option value="restaurant">Ristorante</option>
                  <option value="bar">Bar/Caffè</option>
                  <option value="hotel">Hotel/Albergo</option>
                  <option value="retail">Vendita al Dettaglio</option>
                  <option value="distributor">Distributore</option>
                  <option value="other">Altro</option>
                </select>
                {errors.businessType && <p className="mt-1 text-sm text-red-600">{errors.businessType}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label htmlFor="fiscalCode" className="block text-sm font-medium text-gray-700">
                    Codice Fiscale
                  </label>
                  <input
                    id="fiscalCode"
                    name="fiscalCode"
                    type="text"
                    value={formData.fiscalCode}
                    onChange={handleInputChange}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                    placeholder="Codice Fiscale"
                  />
                </div>

                <div>
                  <label htmlFor="pec" className="block text-sm font-medium text-gray-700">
                    PEC
                  </label>
                  <input
                    id="pec"
                    name="pec"
                    type="email"
                    value={formData.pec}
                    onChange={handleInputChange}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                    placeholder="pec@azienda.it"
                  />
                </div>
              </div>
              
              {/* Codice Univoco */}
              <div className="mt-4">
                <label htmlFor="codiceUnivoco" className="block text-sm font-medium text-gray-700">
                  Codice Univoco (Fatturazione Elettronica)
                </label>
                <input
                  id="codiceUnivoco"
                  name="codiceUnivoco"
                  type="text"
                  value={formData.codiceUnivoco}
                  onChange={handleInputChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm"
                  placeholder="Codice destinatario SDI (es. 0000000)"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Codice di 7 caratteri per la fatturazione elettronica
                </p>
              </div>
            </div>

            {/* Address Fields */}
            <div className="mt-4">
              <h3 className="text-md font-semibold text-gray-700 mb-3">Indirizzo Sede Legale</h3>
              
              {/* Address */}
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  Indirizzo *
                </label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                    errors.address ? 'border-red-500' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                  placeholder="Via/Piazza e numero civico"
                  required
                />
                {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address}</p>}
              </div>

              {/* ZIP Code, City and Province */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
                    CAP *
                  </label>
                  <input
                    id="zipCode"
                    name="zipCode"
                    type="text"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                      errors.zipCode ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                    placeholder="00100"
                    maxLength="5"
                    required
                  />
                  {errors.zipCode && <p className="mt-1 text-sm text-red-600">{errors.zipCode}</p>}
                </div>

                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                    Città *
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                      errors.city ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                    placeholder="Roma"
                    required
                  />
                  {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
                </div>

                <div>
                  <label htmlFor="province" className="block text-sm font-medium text-gray-700">
                    Provincia *
                  </label>
                  <input
                    id="province"
                    name="province"
                    type="text"
                    value={formData.province}
                    onChange={handleInputChange}
                    className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                      errors.province ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm uppercase`}
                    placeholder="RM"
                    maxLength="2"
                    required
                  />
                  {errors.province && <p className="mt-1 text-sm text-red-600">{errors.province}</p>}
                </div>
              </div>
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`appearance-none relative block w-full px-3 py-2 pl-10 pr-10 border ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                    placeholder="Password"
                  />
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Conferma Password *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`appearance-none relative block w-full px-3 py-2 pl-10 pr-10 border ${
                      errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    } placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm`}
                    placeholder="Conferma Password"
                  />
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  id="termsAccepted"
                  name="termsAccepted"
                  type="checkbox"
                  checked={formData.termsAccepted}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="termsAccepted" className="ml-2 block text-sm text-gray-900">
                  Accetto i <Link to="/termini" className="text-purple-600 hover:text-purple-500">termini e condizioni</Link> *
                </label>
              </div>
              {errors.termsAccepted && <p className="text-sm text-red-600">{errors.termsAccepted}</p>}

              <div className="flex items-center">
                <input
                  id="privacyAccepted"
                  name="privacyAccepted"
                  type="checkbox"
                  checked={formData.privacyAccepted}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="privacyAccepted" className="ml-2 block text-sm text-gray-900">
                  Accetto la <Link to="/privacy-policy" className="text-purple-600 hover:text-purple-500">privacy policy</Link> *
                </label>
              </div>
              {errors.privacyAccepted && <p className="text-sm text-red-600">{errors.privacyAccepted}</p>}

              <div className="flex items-center">
                <input
                  id="marketingConsent"
                  name="marketingConsent"
                  type="checkbox"
                  checked={formData.marketingConsent}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="marketingConsent" className="ml-2 block text-sm text-gray-900">
                  Acconsento al trattamento dei dati per finalità di marketing
                </label>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500'
              } transition-colors duration-200`}
            >
              {loading ? 'Registrazione in corso...' : 'Registra Azienda'}
            </button>
          </div>

          <div className="text-center">
            <span className="text-sm text-gray-600">
              Hai già un account?{' '}
              <Link to="/login" className="font-medium text-purple-600 hover:text-purple-500">
                Accedi qui
              </Link>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessRegisterForm;